<template>
  <v-app>
    <app-navigation />

    <v-main
      id="main-content"
      :class="{ 'app-main--offset': isCompactLayout }"
      role="main"
    >
      <router-view />
    </v-main>

    <app-footer />

    <cookie-banner v-if="!showCookieConsent" />
  </v-app>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, h, onBeforeMount, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useAppStore } from '@/stores/app'
import { useMobileStore } from '@/stores/displayStore'
import { usePerformanceMonitoring } from '@/composables/usePerformanceMonitoring'
import { getCookies } from 'typescript-cookie'
import AppNavigation from '@/components/app/navigation/AppNavigation.vue'
import AppFooter from '@/components/app/footer/AppFooter.vue'

const CookieBanner = defineAsyncComponent({
  loader: () => import('@/components/ui/CookieBanner.vue'),
  loadingComponent: () => h('div', 'Loading cookie banner...'), // Optional loading state
  delay: 200, // Delay before showing loading component
  timeout: 3000 // Timeout for loading
})

const appStore = useAppStore()
const mobileStore = useMobileStore()
const { width, mobile, smAndUp, mdAndDown } = useDisplay()
const showCookieConsent = ref(false)
const isCompactLayout = computed(() => mobileStore.mobile || mobileStore.tablet)

// Enable performance monitoring (logs in development, silent in production)
usePerformanceMonitoring({
  enableLogging: import.meta.env.DEV
})

const setMobile = () => {
  // Treat phones as "mobile" and sm/md widths as "tablet"
  mobileStore.setMobile(mobile.value)
  mobileStore.setTablet(!mobile.value && mdAndDown.value && smAndUp.value)
}

appStore.$subscribe((mutation, state) => {
  if (mutation.type === 'direct') {
    showCookieConsent.value = !!parseInt(state.cookies['hide-banner'])
  }
})

watch(width, () => {
  setMobile()
})

onBeforeMount(() => {
  const cookies = getCookies()
  appStore.setCookies(cookies)
  showCookieConsent.value = !!parseInt(appStore.getCookies()['hide-banner'])

  setMobile()

  appStore.setElementSize({
    nav: 150,
    footer: mobileStore.mobile && !mobileStore.tablet ? 400 : 180
  })

  // Initialize bundled data once
  appStore.initialize()
})
</script>

<style lang="scss">
* {
  font-family: Roboto, sans-serif;
}

// Focus styles for keyboard navigation
*:focus-visible {
  outline: 2px solid var(--v-primary-base, #821305);
  outline-offset: 2px;
}

// Remove focus styles for mouse users
*:focus:not(:focus-visible) {
  outline: none;
}

// Enhanced focus for interactive elements
a:focus-visible,
button:focus-visible,
.v-btn:focus-visible {
  outline: 2px solid var(--v-primary-base, #821305);
  outline-offset: 3px;
}

p {
  font-size: 18px;
  color: var(--night);
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: Hokyaa, sans-serif;
  color: var(--night);
}

// Remove default Vuetify padding since app-bar is static
.v-main {
  padding-top: 0 !important;
}

.app-main--offset {
  margin-top: 100px;
}

//<!-- Sources: -->
//<!-- https://www.falkenklinge.de/ -->
//<!-- https://www.wuerttembergerritter.de/ -->
//<!-- https://www.unterfreyembanner.de/ -->
</style>
